// =============================================================================
//  Image revision + freshness helpers
// =============================================================================
//
//  Accepted product cutouts live at a STABLE CDN path. After a staged refresh
//  promotes a new backgroundless WebP onto that path, `products.image_revision`
//  increments. Renderers append `?v=<revision>` so the browser fetches the new
//  bytes immediately; the canonical object path (and its SEO authority) never
//  changes. Cloudflare's edge still keys on pathname, so the query is a
//  client-cache buster only.
//
//  Processed images are due for a maintenance refresh 30 days after
//  `image_succeeded_at`. Provisional images come due sooner. Missing clocks
//  (legacy rows the overlay did not populate) are treated as NOT due so a
//  deploy cannot stampede find-product.
// =============================================================================

function envPositiveDaysMs(name: string, fallbackDays: number): number {
  const n = Number.parseInt(process.env[name] ?? '', 10);
  const days = Number.isFinite(n) && n > 0 ? n : fallbackDays;
  return days * 24 * 60 * 60 * 1000;
}

/** Must stay in lockstep with gateway product-cache.ts (same env names). */
export const PROCESSED_IMAGE_TTL_MS = envPositiveDaysMs('IMAGE_REFRESH_TTL_DAYS', 30);
export const PROVISIONAL_IMAGE_TTL_MS = envPositiveDaysMs(
  'IMAGE_PROVISIONAL_REFRESH_DAYS',
  14,
);

export function isAcceptedImageStatus(
  status: string | null | undefined,
): boolean {
  return status === 'processed' || status === 'provisional';
}

/**
 * Append `?v=<revision>` to a first-party image URL. No-op when revision is
 * missing or zero (never promoted under the staged pipeline).
 */
export function withImageRevision(
  url: string,
  revision: number | null | undefined,
): string {
  if (!url || !revision || revision <= 0) return url;
  try {
    const parsed = new URL(url);
    parsed.searchParams.set('v', String(revision));
    return parsed.toString();
  } catch {
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}v=${revision}`;
  }
}

/**
 * Render URL for a live products-row update after promotion. Returns null when
 * the row is not an accepted cutout or the revision has not increased, so a
 * pending refresh never replaces the image the page is already showing.
 */
export function promotedImageSrc(opts: {
  imageUrl: string | null | undefined;
  status: string | null | undefined;
  revision: number | null | undefined;
  previousRevision?: number;
}): string | null {
  if (!opts.imageUrl || !isAcceptedImageStatus(opts.status)) return null;
  const revision = Number(opts.revision ?? 0) || 0;
  const previous = opts.previousRevision ?? 0;
  if (revision <= previous) return null;
  return withImageRevision(opts.imageUrl, revision);
}

function succeededAtMs(value: string | Date | null | undefined): number | null {
  if (!value) return null;
  const ms = value instanceof Date ? value.getTime() : Date.parse(value);
  return Number.isFinite(ms) ? ms : null;
}

/**
 * True when an accepted image should be submitted for a staged replacement.
 * `force` (load failure / admin "wrong image") bypasses the age check.
 */
export function isProcessedImageDue(opts: {
  succeededAt?: string | Date | null;
  status?: string | null;
  force?: boolean;
}): boolean {
  if (opts.force) return true;
  const status = opts.status ?? 'processed';
  if (!isAcceptedImageStatus(status)) return false;
  const at = succeededAtMs(opts.succeededAt);
  if (at === null) return false;
  const ttl =
    status === 'provisional' ? PROVISIONAL_IMAGE_TTL_MS : PROCESSED_IMAGE_TTL_MS;
  return Date.now() - at >= ttl;
}
